import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Field, Form } from 'formik';

import { Card } from 'components/Card/Card';
import Button from 'components/Button/Button';
import Loading from 'components/Loading/Loading';
import Tooltip from 'components/Tooltip/Tooltip';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import colors from 'theme/Colors.module.scss';
import './CreateCompanyParam.scss';

import {
  Box,
  Flex,
  Text,
  Input,
  FormLabel,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';

import * as paramsHelpers from 'helpers/params';

import {
  useGetCompanyParamByIdQuery,
  useCreateCompanyParamMutation,
  useUpdateCompanyParamMutation,
} from 'modules/params/companyParamsApi';

const initialFormState = {
  key: '',
  label: '',
  scopes: ['audience', 'template'],
};

export default function CreateCompanyParam() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const id = pathname.split('/')[2];

  const [initialForm, setInitialForm] = useState(initialFormState);

  // Selectors
  const sessionUser = useSelector(state => state.app.user);

  const {
    data: companyParamData,
    error,
    isLoading,
  } = useGetCompanyParamByIdQuery(id, {
    skip: !id || id === 'create', // Skip the query if Id is null or undefined
  });

  const [createCompanyParam] = useCreateCompanyParamMutation();
  const [updateCompanyParam] = useUpdateCompanyParamMutation();

  const handleCreateOrUpdateParam = async values => {
    const scopes = {
      audience: ['audience'],
      template: ['template'],
      all: ['audience', 'template'],
    };

    const params = {
      ...values,
      scopes: scopes[values.scopes],
      company_id: sessionUser.company_id,
    };

    if (id === 'create') {
      try {
        await createCompanyParam(params).unwrap();
        navigate('/companyParams');
        dispatch(
          pushNotificationWithTimeout({
            type: 'success',
            message: 'Param created successfully',
          })
        );
      } catch (error) {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: 'Param already exist',
          })
        );
      }
    } else {
      try {
        await updateCompanyParam({ id, data: params }).unwrap();
        dispatch(
          pushNotificationWithTimeout({
            type: 'success',
            message: 'Param updated successfully',
          })
        );
      } catch (error) {
        dispatch(
          pushNotificationWithTimeout({
            type: 'error',
            message: 'Failed to update param',
          })
        );
      }
    }
  };

  useEffect(() => {
    setInitialForm(
      paramsHelpers.setFormInitialValues(companyParamData || initialFormState)
    );
  }, [companyParamData]);

  if (error) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to fetch param data',
      })
    );
  }

  if (isLoading) {
    return <Loading type="fullpage" />;
  }

  return (
    <>
      <Box className="create-params-header">
        <Box className="create-params-header__left">
          <Button
            type="button"
            url="/companyParams"
            icon="back"
            text="Back"
            textColor={colors.slate}
            btnStyle="transparent"
          />
        </Box>
        <Box className="create-params-header__nav">
          <Text as="p" fontWeight="600">
            Configure Params Manager
          </Text>
        </Box>
        <Box className="create-params-header__right"></Box>
      </Box>

      <Formik
        initialValues={initialForm}
        validationSchema={paramsHelpers.setValidationSchema()}
        onSubmit={handleCreateOrUpdateParam}
        enableReinitialize
      >
        {({ values, setFieldValue }) => {
          const onScopeChecked = scope => {
            const { scopes } = values;
            const isAudience = scope === 'audience';
            const options = {
              '': isAudience ? 'audience' : 'template',
              audience: isAudience ? '' : 'all',
              template: isAudience ? 'all' : '',
              all: isAudience ? 'template' : 'audience',
            };
            setFieldValue('scopes', options[scopes]);
          };

          return (
            <Form className="create-params-form__container">
              <Card className="create-params-form__card">
                <Text as="p" fontWeight="600">
                  Configure Params
                </Text>
                <Flex flexDirection="column" gap={8} mt={8}>
                  <Flex gap={8} flexWrap="wrap">
                    <Flex flexDirection="column" flex="1 1 200px" minW="0">
                      <Field name="key">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={form.errors.key && form.touched.key}
                          >
                            <FormLabel>
                              key&nbsp;
                              <Tooltip
                                tipText="Param unique key"
                                direction="right"
                              />
                            </FormLabel>
                            <Input
                              {...field}
                              placeholder="key"
                              focusBorderColor={colors.primary600}
                            />
                            <FormErrorMessage>
                              {form.errors.key}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>
                    <Flex flexDirection="column" flex="1 1 200px" minW="0">
                      <Field name="label">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={form.errors.label && form.touched.label}
                          >
                            <FormLabel>
                              Label &nbsp;
                              <Tooltip
                                tipText="Param label"
                                direction="right"
                              />
                            </FormLabel>
                            <Input
                              {...field}
                              placeholder="label"
                              focusBorderColor={colors.primary600}
                            />
                            <FormErrorMessage>
                              {form.errors.label}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>
                    <Flex flexDirection="column" flex="1 1 200px" minW="0">
                      <Field name="scopes">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.scopes && form.touched.scopes
                            }
                          >
                            <FormLabel>
                              Scopes &nbsp;
                              <Tooltip
                                tipText="Whether the parameter should be available to audiences, templates, or both"
                                direction="right"
                              />
                            </FormLabel>
                            <fieldset
                              style={{
                                padding: '8px 0px',
                              }}
                            >
                              <input
                                type="checkbox"
                                name="scopes"
                                value="audience"
                                style={{ cursor: 'pointer' }}
                                checked={
                                  values.scopes === 'audience' ||
                                  values.scopes === 'all'
                                }
                                onChange={ev => onScopeChecked(ev.target.value)}
                              />
                              <label
                                htmlFor="audience-input"
                                style={{
                                  cursor: 'pointer',
                                  marginLeft: '5px',
                                }}
                              >
                                Audience
                              </label>
                              <input
                                type="checkbox"
                                name="scopes"
                                value="template"
                                style={{
                                  cursor: 'pointer',
                                  marginLeft: '1rem',
                                }}
                                checked={
                                  values.scopes === 'template' ||
                                  values.scopes === 'all'
                                }
                                onChange={ev => onScopeChecked(ev.target.value)}
                              />
                              <label
                                htmlFor="template-input"
                                style={{
                                  cursor: 'pointer',
                                  marginLeft: '5px',
                                }}
                              >
                                Template
                              </label>
                            </fieldset>
                            <FormErrorMessage>
                              {form.errors.scopes}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>

              <Flex flexDirection="column" align="center">
                <Button
                  type="submit"
                  text="Update Param"
                  textColor={colors.white}
                  btnStyle="solid"
                  hoverColor={colors.primary500}
                  textHoverColor={colors.white}
                />
              </Flex>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
